'use client';

import { Carousel, CarouselApi, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';

import HoverPrefetchLink from '@/components/hover-prefetch-link';
import { COLORS_FAMILY_KEY } from '@/constants/colors-family-key';
import { websiteEnv } from '@/constants/website-env';
import useIsMobile from '@/hooks/use-is-mobile';
import { cn } from '@/lib/utils';
import { Popover, PopoverAnchor, PopoverArrow, PopoverContent } from './ui/popover';
import { Tooltip, TooltipArrow, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

type Aggregation = NonNullable<NonNullable<GetPageShopByColorQuery['allCategories']>['aggregations']>[0];
type ColorsFamilyOption = NonNullable<
  NonNullable<NonNullable<NonNullable<GetPageShopByColorQuery['products']>['aggregations']>[0]>['options']
>[0];

const ShopByColorsFamily = ({
  colorsFamilyOptions,
  aggregations,
  className,
  categoryUrlKey,
}: {
  colorsFamilyOptions: ColorsFamilyOption[];
  aggregations: Aggregation[];
  className?: string;
  categoryUrlKey: string;
}) => {
  const isMobile = useIsMobile();
  const colorAggregationIdMap = aggregations
    ? aggregations.reduce<Record<string, Aggregation>>((acc, cur) => {
        if (cur?.code !== 'color' || !cur.option_id) {
          return acc;
        }

        acc[cur.option_id] = cur;

        return acc;
      }, {})
    : {};
  const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [slidesLessThanViewport, setSlidesLessThanViewport] = useState(false);

  const handlePopoverOpen = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const element = e.target;
    const container = scrollContainerRef.current;

    if (container && element && element instanceof HTMLDivElement) {
      const containerRect = container.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      const scrollLeft = container.scrollLeft;
      const elementOffsetLeft = elementRect.left - containerRect.left;
      const centerPosition = elementOffsetLeft + elementRect.width / 2 - containerRect.width / 2;

      container.scrollTo({
        left: scrollLeft + centerPosition,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    if (!carouselApi) {
      return;
    }

    const onSelect = () => {
      setCanScrollPrev(carouselApi.canScrollPrev());
      setCanScrollNext(carouselApi.canScrollNext());
    };

    const initHandler = () => {
      const { containerRect, slideRects } = carouselApi.internalEngine() || {};
      const lastSlideRect = slideRects ? [...slideRects].pop() : null;
      const slidesLessThanViewport = !!containerRect && !!lastSlideRect && containerRect.right > lastSlideRect.right;
      setCanScrollPrev(carouselApi.canScrollPrev());
      setCanScrollNext(carouselApi.canScrollNext());
      setSlidesLessThanViewport(slidesLessThanViewport);
    };

    onSelect();
    initHandler();
    carouselApi.on('reInit', initHandler);
    carouselApi.on('select', onSelect);

    return () => {
      carouselApi?.off('select', onSelect);
      carouselApi?.off('reInit', initHandler);
    };
  }, [carouselApi]);

  const renderColorsFamilyNodes = (inCarousel: boolean = false) => {
    return colorsFamilyOptions?.map((colorsFamily, index) => {
      if (!colorsFamily) {
        return null;
      }

      const values = colorsFamily.value?.split(',');
      const subColorOptions = values?.reduce<Aggregation[]>((acc, cur) => {
        const aggregationValue = colorAggregationIdMap[cur];

        if (aggregationValue) {
          acc.push(aggregationValue);
        }

        return acc;
      }, []);

      if (!Array.isArray(subColorOptions) || subColorOptions.length === 0) {
        return null;
      }

      if (inCarousel) {
        return (
          <CarouselItem
            key={index}
            className='basis-[95px] cursor-pointer pl-4 pr-px '
          >
            <ShopByColorsFamilyItem
              key={index}
              swatchData={colorsFamily.swatch_data || null}
              colorsFamilyLabel={colorsFamily.label || ''}
              subColorOptions={subColorOptions}
              onPopoverOpen={handlePopoverOpen}
              categoryUrlKey={categoryUrlKey}
            />
          </CarouselItem>
        );
      }

      return (
        <ShopByColorsFamilyItem
          key={index}
          swatchData={colorsFamily.swatch_data || null}
          colorsFamilyLabel={colorsFamily.label || ''}
          subColorOptions={subColorOptions}
          onPopoverOpen={handlePopoverOpen}
          categoryUrlKey={categoryUrlKey}
        />
      );
    });
  };

  const filterSection = isMobile ? (
    <div className='overflow-hidden'>
      <div
        className=' -mb-5 overflow-auto pb-5'
        ref={scrollContainerRef}
      >
        <div className='flex items-center gap-x-5 pl-2.5'>{renderColorsFamilyNodes()}</div>
      </div>
    </div>
  ) : (
    <div className='relative'>
      <div className={clsx('mx-auto flex max-w-site items-start gap-x-[15px] px-1.5')}>
        <CarouselPrevBtn
          onClick={() => {
            carouselApi?.scrollPrev();
          }}
          className={clsx(!canScrollPrev && 'invisible', ' flex h-[66px] w-10 items-center justify-center')}
        />
        <div className='flex-1 overflow-hidden'>
          <Carousel
            className='w-full'
            opts={{
              align: 'start',
            }}
            setApi={setCarouselApi}
          >
            <CarouselContent
              className={clsx('-ml-[15px] flex', slidesLessThanViewport ? 'justify-center' : 'justify-start')}
            >
              {renderColorsFamilyNodes(true)}
            </CarouselContent>
          </Carousel>
        </div>
        <CarouselNextBtn
          onClick={() => {
            carouselApi?.scrollNext();
          }}
          className={clsx(!canScrollNext && 'invisible', ' flex h-[66px] w-10 items-center justify-center')}
        />
      </div>
    </div>
  );

  return (
    <div className={cn('mt-10 bg-f8f3ef py-[30px] lg_py-[100px]', className)}>
      <h2 className='text-center font-Shango text-[26px] leading-8 lg_text-[30px] lg_leading-9'>
        {websiteEnv.isUnitedKingdom ? 'SHOP BY COLOUR' : 'SHOP BY COLOR'}
      </h2>
      <div className='mt-5 lg_mt-[35px]'>{filterSection}</div>
    </div>
  );
};

const CarouselPrevBtn = (props: React.HtmlHTMLAttributes<HTMLButtonElement>) => {
  return (
    <button
      type='button'
      {...props}
      aria-label='scroll prev'
    >
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='13'
        height='22'
        fill='none'
        viewBox='0 0 13 22'
      >
        <path
          stroke='#80583F'
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth='2'
          d='m11.115 1-9.23 10 9.23 10'
        />
      </svg>
    </button>
  );
};

const CarouselNextBtn = (props: React.HtmlHTMLAttributes<HTMLButtonElement>) => {
  return (
    <button
      type='button'
      {...props}
      aria-label='scroll next'
    >
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='13'
        height='22'
        fill='none'
        viewBox='0 0 13 22'
      >
        <path
          stroke='#80583F'
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth='2'
          d='m1.885 21 9.23-10-9.23-10'
        />
      </svg>
    </button>
  );
};

const ShopByColorsFamilyItem = ({
  colorsFamilyLabel,
  subColorOptions,
  onPopoverOpen,
  swatchData,
  categoryUrlKey,
}: {
  colorsFamilyLabel: string;
  swatchData: NonNullable<ColorsFamilyOption>['swatch_data'];
  subColorOptions: any[];
  onPopoverOpen: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  categoryUrlKey: string;
}) => {
  const isMobile = useIsMobile();
  const colorsFamilyUrlRewrite = colorsFamilyLabel.toLowerCase().replace(/\s/g, '-') || '';
  const selectedAllLinkHref = `${categoryUrlKey}/${COLORS_FAMILY_KEY}/${colorsFamilyUrlRewrite}`;
  const triggerRef = useRef<HTMLDivElement>(null);
  const isColorAll = colorsFamilyUrlRewrite === 'all';
  const [popoverVisible, setPopoverVisible] = useState(false);

  const colorRectStyle = createColorStyle(swatchData?.thumbnail || '', swatchData?.value || '');

  const triggerNode = (
    <div
      className='flex-none'
      ref={triggerRef}
      onClick={e => {
        if (isMobile) {
          onPopoverOpen(e);
          setPopoverVisible(true);
        }
      }}
    >
      <div className='cursor-pointer  rounded-[10px] border-1 border-solid border-transparent p-1 '>
        {isColorAll || isMobile ? (
          <div
            style={isColorAll ? undefined : colorRectStyle}
            className={cn(
              'block h-[50px] w-[50px] rounded-[5px] bg-contain lg_h-[66px] lg_w-[66px]',
              isWhite(swatchData?.value || '') ? 'border-1 border-E7DED5' : '',
            )}
          >
            {isColorAll ? (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='67'
                height='66'
                fill='none'
                viewBox='0 0 67 66'
                className='h-auto w-full'
              >
                <g clipPath='url(#clip0_13530_35876)'>
                  <path
                    fill='#EFEFE6'
                    d='M.5 0h22v22H.5z'
                  />
                  <path
                    fill='#E0D1BD'
                    d='M22.5 0h22v22h-22z'
                  />
                  <path
                    fill='#D68B59'
                    d='M44.5 0h22v22h-22z'
                  />
                  <path
                    fill='#F0D3C9'
                    d='M.5 22h22v22H.5z'
                  />
                  <path
                    fill='#CC8986'
                    d='M22.5 22h22v22h-22z'
                  />
                  <path
                    fill='#9A1834'
                    d='M44.5 22h22v22h-22z'
                  />
                  <path
                    fill='#C4C3D0'
                    d='M.5 44h22v22H.5z'
                  />
                  <path
                    fill='#6F6E71'
                    d='M22.5 44h22v22h-22z'
                  />
                  <path
                    fill='#002712'
                    d='M44.5 44h22v22h-22z'
                  />
                </g>
                <defs>
                  <clipPath id='clip0_13530_35876'>
                    <rect
                      width='66'
                      height='66'
                      x='0.5'
                      fill='#fff'
                      rx='5'
                    />
                  </clipPath>
                </defs>
              </svg>
            ) : null}
          </div>
        ) : (
          <HoverPrefetchLink
            style={colorRectStyle}
            href={selectedAllLinkHref}
            className={cn(
              ' block h-[50px] w-[50px] rounded-[5px] bg-contain lg_h-[66px] lg_w-[66px]',
              isWhite(swatchData?.value || '') ? 'border-1 border-E7DED5' : '',
            )}
            aria-label={colorsFamilyLabel}
          />
        )}
      </div>
      <div className={clsx('mt-[5px]', ' text-center text-[15px] leading-5', 'cursor-pointer')}>
        {colorsFamilyLabel}
      </div>
    </div>
  );

  const dropdownNode = (
    <div
      className={clsx(
        'max-h-[320px]',
        'list-none gap-x-[10px] gap-y-6 overflow-y-auto overscroll-none ',
        'px-1.5 py-4 lg_px-2.5',
        'category-color-select',
      )}
    >
      {!isColorAll && (
        <HoverPrefetchLink
          href={selectedAllLinkHref}
          className='mb-[15px] ml-2.5 flex items-center gap-3'
        >
          <span
            className={clsx(
              'h-5 w-5',
              'border-1 border-solid border-80583F bg-transparent',
              'flex items-center justify-center',
            )}
          />
          <span className='text-sm leading-5 text-#403732'>SELECT ALL</span>
        </HoverPrefetchLink>
      )}

      <div
        className={clsx(
          'flex flex-wrap',
          subColorOptions.length > 5 && 'lg_grid lg_grid-cols-5',
          ' max-w-[calc(100vw_-_34px)] lg_max-w-none',
          'gap-y-2.5',
          isColorAll ? 'gap-x-[9px]' : 'gap-x-2.5',
        )}
      >
        {subColorOptions.map((colorOption: any, index: number) => {
          if (!colorOption) {
            return null;
          }

          const colorRewrite = colorOption.rewrite;
          const swatch_data = colorOption.swatch_data;
          const nextSubColorLink = `${categoryUrlKey}/color/${colorRewrite}`;
          const colorRectStyle = createSubColorStyle(swatch_data?.thumbnail, swatch_data?.value);

          return (
            <HoverPrefetchLink
              href={nextSubColorLink}
              key={index}
              className='group flex cursor-pointer flex-col items-center justify-start gap-[10px]  '
            >
              <div className=' h-10 w-10 rounded-[50%] border-2 border-solid border-transparent p-[3px] lg_group-hover_border-80583F'>
                <div
                  style={colorRectStyle}
                  className={cn(
                    'h-full rounded-[50%] bg-contain ',
                    isWhite(swatch_data?.value || '') ? 'border-1 border-E7DED5' : '',
                  )}
                />
              </div>

              <span
                className={clsx(
                  ' text-center text-[13px] leading-5 text-#403732 lg_group-hover_text-80583F ',
                  'w-[60px] lg_w-[76px]',
                )}
              >
                {websiteEnv.isUnitedKingdom && colorOption?.label === 'Multi Color'
                  ? 'Multicolour'
                  : colorOption?.label}
              </span>
            </HoverPrefetchLink>
          );
        })}
      </div>
    </div>
  );

  return isMobile ? (
    <Popover
      open={popoverVisible}
      onOpenChange={setPopoverVisible}
    >
      <PopoverAnchor asChild>{triggerNode}</PopoverAnchor>
      <PopoverContent
        className='z-[200] rounded-[10px] bg-white shadow-[0_0_10px_0_rgba(0,0,0,0.1)]'
        side='bottom'
        collisionPadding={{
          left: 10,
          right: 10,
        }}
      >
        {dropdownNode}
        <PopoverArrow className='h-auto w-4 fill-white' />
      </PopoverContent>
    </Popover>
  ) : (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger
          onClick={e => {
            if (isColorAll) {
              e.preventDefault();
            }
          }}
          asChild
        >
          {triggerNode}
        </TooltipTrigger>
        <TooltipContent
          sideOffset={0}
          side='bottom'
          className='rounded-[10px] border-none bg-white p-0 shadow-[0_0_10px_0_rgba(0,0,0,0.1)] outline-0'
          onPointerDownOutside={e => {
            if (triggerRef.current?.contains(e.target as HTMLElement) && isColorAll) {
              e.preventDefault();
            }
          }}
        >
          <TooltipArrow className='h-auto w-4 fill-white' />
          {dropdownNode}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const isWhite = (value: string) => {
  const lowercaseValue = value.toLowerCase();

  return lowercaseValue === '#fff' || lowercaseValue === '#ffffff';
};

const createColorStyle = (thumbnail: string | undefined, value: string | undefined) => {
  if (thumbnail && thumbnail.indexOf('#') < 0) {
    return {
      backgroundImage: `url("${thumbnail}")`,
    };
  }

  return {
    backgroundColor: value,
  };
};

const createSubColorStyle = (thumbnail: string | undefined, value: string | undefined) => {
  if (thumbnail && thumbnail.indexOf('#') < 0) {
    return {
      backgroundImage: `url("${process.env.NEXT_PUBLIC_BASE_PATH}/media/attribute/swatch/swatch_thumb/110x90${thumbnail}")`,
    };
  }

  return {
    backgroundColor: value,
  };
};

export default ShopByColorsFamily;
